import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save, ArrowLeft, Loader2, MapPin, ChevronDown, Upload, X, Image as ImageIcon } from 'lucide-react'
import useMerchantStore from '@/stores/merchantStore'
import { getImageUrl } from '@/config/imageConfig'
import useCountryStore from '@/stores/countryStore'
import { CreateMerchantDto, UpdateMerchantDto } from '@/services/merchantService'
import OrganizationSelector from './OrganizationSelector'
import RegionSelector from './RegionSelector'
import StoreSelector from './StoreSelector'
import { useInputValidation, businessNameRules, businessTypeRules, taxIdRules, descriptionRules, codeRules, getInputClassName } from '@/hooks/useInputValidation'
import { GoogleMapsAddressPicker, GoogleMapsStaticMap, GoogleMapsConfigProvider } from '@/components/maps'
import { ParsedAddress } from '@/components/maps/types'
import { DetailSection } from '../ui/DetailSection'

const MerchantForm = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditMode = !!id

  const {
    currentMerchant,
    loading,
    error,
    fetchMerchantById,
    createMerchant,
    updateMerchant,
    clearError,
    clearCurrentMerchant
  } = useMerchantStore()

  const { countries, fetchCountries } = useCountryStore()

  // Validation hooks for each field
  const businessNameValidation = useInputValidation('', businessNameRules)
  const businessDescriptionValidation = useInputValidation('', descriptionRules)
  const businessTypeValidation = useInputValidation('', businessTypeRules)
  const taxIdValidation = useInputValidation('', taxIdRules)
  const storeCodeValidation = useInputValidation('', codeRules)
  const addressLine1Validation = useInputValidation('', { required: true, minLength: 3, maxLength: 255 })
  const cityValidation = useInputValidation('', { required: true, minLength: 2, maxLength: 100 })
  const postalCodeValidation = useInputValidation('', { required: true, minLength: 2, maxLength: 20 })
  const countryValidation = useInputValidation('', { required: true })

  const [formData, setFormData] = useState<CreateMerchantDto | UpdateMerchantDto>({
    business_name: '',
    business_description: '',
    business_type: '',
    tax_id: '',
    is_active: true,
    // Corporate hierarchy fields
    organization_id: '',
    org_region_id: '',
    store_code: '',
    parent_store_id: '',
    // Address fields
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    latitude: undefined,
    longitude: undefined,
    is_primary: true
  })

  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [showAdvancedGeolocation, setShowAdvancedGeolocation] = useState(false)
  const [showMap, setShowMap] = useState(false)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)

  useEffect(() => {
    // Fetch countries on component mount
    fetchCountries({ limit: 250, enabled: true })

    if (isEditMode && id) {
      fetchMerchantById(id)
    }

    return () => {
      clearCurrentMerchant()
      clearError()
    }
  }, [id, isEditMode])

  // Sync validation hooks with form data when editing
  useEffect(() => {
    if (isEditMode && currentMerchant) {
      setFormData({
        business_name: currentMerchant.business_name,
        business_description: currentMerchant.business_description || '',
        business_type: currentMerchant.business_type,
        tax_id: currentMerchant.tax_id || '',
        is_active: currentMerchant.is_active,
        // Corporate hierarchy fields
        organization_id: currentMerchant.organization_id || '',
        org_region_id: currentMerchant.org_region_id || '',
        store_code: currentMerchant.store_code || '',
        parent_store_id: currentMerchant.parent_store_id || '',
        // Address fields
        address_line1: currentMerchant.address_line1 || '',
        address_line2: currentMerchant.address_line2 || '',
        city: currentMerchant.city || '',
        state: currentMerchant.state || '',
        postal_code: currentMerchant.postal_code || '',
        country: currentMerchant.country || '',
        latitude: currentMerchant.latitude || undefined,
        longitude: currentMerchant.longitude || undefined,
        is_primary: currentMerchant.is_primary !== undefined ? currentMerchant.is_primary : true
      })
      
      // Update validation hooks with initial values
      businessNameValidation.setValue(currentMerchant.business_name)
      businessDescriptionValidation.setValue(currentMerchant.business_description || '')
      businessTypeValidation.setValue(currentMerchant.business_type)
      taxIdValidation.setValue(currentMerchant.tax_id || '')
      storeCodeValidation.setValue(currentMerchant.store_code || '')
      addressLine1Validation.setValue(currentMerchant.address_line1 || '')
      cityValidation.setValue(currentMerchant.city || '')
      postalCodeValidation.setValue(currentMerchant.postal_code || '')
      countryValidation.setValue(currentMerchant.country || '')

      // Load existing logo preview
      if (currentMerchant.logo_url) {
        setLogoPreview(getImageUrl(`/uploads/merchants/${currentMerchant.logo_url}`))
      } else {
        setLogoPreview(null)
      }
      setLogoFile(null)
    }
  }, [currentMerchant, isEditMode])

  // Update form data when validation hooks change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      business_name: businessNameValidation.value,
      business_description: businessDescriptionValidation.value,
      business_type: businessTypeValidation.value,
      tax_id: taxIdValidation.value,
      store_code: storeCodeValidation.value,
      address_line1: addressLine1Validation.value,
      city: cityValidation.value,
      postal_code: postalCodeValidation.value,
      country: countryValidation.value
    }))
  }, [
    businessNameValidation.value,
    businessDescriptionValidation.value,
    businessTypeValidation.value,
    taxIdValidation.value,
    storeCodeValidation.value,
    addressLine1Validation.value,
    cityValidation.value,
    postalCodeValidation.value,
    countryValidation.value
  ])

  const validateForm = (): boolean => {
    // Validate all fields
    const businessNameValid = businessNameValidation.validate()
    const businessTypeValid = businessTypeValidation.validate()
    const storeCodeValid = storeCodeValidation.validate()
    const addressLine1Valid = addressLine1Validation.validate()
    const cityValid = cityValidation.validate()
    const postalCodeValid = postalCodeValidation.validate()
    const countryValid = countryValidation.validate()
    
    return businessNameValid && businessTypeValid && storeCodeValid &&
           addressLine1Valid && cityValid && postalCodeValid && countryValid
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setLogoFile(file)
      // Create a preview URL
      setLogoPreview(URL.createObjectURL(file))
    }
  }

  const handleRemoveLogo = () => {
    setLogoFile(null)
    setLogoPreview(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      const payload = { ...formData } as CreateMerchantDto
      // Only attach the logo when a new file was selected
      if (logoFile) {
        payload.logo = logoFile
      }

      if (isEditMode && id) {
        await updateMerchant(id, payload as UpdateMerchantDto)
      } else {
        await createMerchant(payload)
      }
      navigate('/merchants')
    } catch (error) {
      console.error('Error saving merchant:', error)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    const fieldValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    
    // Update the appropriate validation hook
    switch (name) {
      case 'business_name':
        businessNameValidation.handleChange(e)
        break
      case 'business_description':
        businessDescriptionValidation.handleChange(e)
        break
      case 'business_type':
        businessTypeValidation.handleChange(e)
        break
      case 'tax_id':
        taxIdValidation.handleChange(e)
        break
      case 'address_line1':
        addressLine1Validation.handleChange(e)
        break
      case 'city':
        cityValidation.handleChange(e)
        break
      case 'postal_code':
        postalCodeValidation.handleChange(e)
        break
      case 'country':
        countryValidation.handleChange(e)
        break
      default:
        // For other fields, update form data directly
        setFormData(prev => ({
          ...prev,
          [name]: fieldValue
        }))
    }
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleAddressSelect = (address: ParsedAddress | null) => {
    if (!address) {
      // Clear address if null
      setFormData(prev => ({
        ...prev,
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        latitude: undefined,
        longitude: undefined
      }))
      setShowMap(false)
      return
    }

    // Update form data with the selected address
    setFormData(prev => ({
      ...prev,
      address_line1: address.address_line1 || '',
      address_line2: address.address_line2 || '',
      city: address.city || '',
      state: address.state || '',
      postal_code: address.postal_code || '',
      country: address.country || '',
      latitude: address.latitude || undefined,
      longitude: address.longitude || undefined
    }))

    // Update validation hooks
    addressLine1Validation.setValue(address.address_line1 || '')
    cityValidation.setValue(address.city || '')
    postalCodeValidation.setValue(address.postal_code || '')
    countryValidation.setValue(address.country || '')

    // Show the map
    setShowMap(true)
  }

  const businessTypes = [
    'Restaurant',
    'Café',
    'Boutique',
    'Supermarket',
    'Pharmacy',
    'Salon de beauté',
    'Gym',
    'Hôtel',
    'Autre'
  ]

  if (isEditMode && loading && !currentMerchant) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Chargement du commerçant...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/merchants')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour à la liste
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Modifier le commerçant' : 'Nouveau commerçant'}
          </h1>
          <p className="text-gray-600">
            {isEditMode 
              ? 'Modifiez les informations du commerçant' 
              : 'Ajoutez un nouveau commerçant à la plateforme'}
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Business Information */}
          <DetailSection title="Informations du commerce" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du commerce *
              </label>
              <input
                type="text"
                name="business_name"
                value={businessNameValidation.value}
                onChange={businessNameValidation.handleChange}
                onBlur={businessNameValidation.handleBlur}
                className={getInputClassName(
                  !!businessNameValidation.error,
                  businessNameValidation.isValid && businessNameValidation.touched,
                  "w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                )}
                placeholder="Ex: Café de la Place"
              />
              {businessNameValidation.error && (
                <p className="mt-1 text-sm text-red-600">{businessNameValidation.error}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="business_description"
                value={businessDescriptionValidation.value}
                onChange={businessDescriptionValidation.handleChange}
                onBlur={businessDescriptionValidation.handleBlur}
                rows={3}
                className={getInputClassName(
                  !!businessDescriptionValidation.error,
                  businessDescriptionValidation.isValid && businessDescriptionValidation.touched,
                  "w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                )}
                placeholder="Description du commerce..."
              />
              {businessDescriptionValidation.error && (
                <p className="mt-1 text-sm text-red-600">{businessDescriptionValidation.error}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de commerce *
              </label>
              <select
                name="business_type"
                value={businessTypeValidation.value}
                onChange={businessTypeValidation.handleChange}
                onBlur={businessTypeValidation.handleBlur}
                className={getInputClassName(
                  !!businessTypeValidation.error,
                  businessTypeValidation.isValid && businessTypeValidation.touched,
                  "w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                )}
              >
                <option value="">Sélectionnez un type</option>
                {businessTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {businessTypeValidation.error && (
                <p className="mt-1 text-sm text-red-600">{businessTypeValidation.error}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numéro de TVA/TAX ID
              </label>
              <input
                type="text"
                name="tax_id"
                value={taxIdValidation.value}
                onChange={taxIdValidation.handleChange}
                onBlur={taxIdValidation.handleBlur}
                className={getInputClassName(
                  !!taxIdValidation.error,
                  taxIdValidation.isValid && taxIdValidation.touched,
                  "w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                )}
                placeholder="Ex: FR12345678901"
              />
              {taxIdValidation.error && (
                <p className="mt-1 text-sm text-red-600">{taxIdValidation.error}</p>
              )}
            </div>

            {/* Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Logo du commerce
              </label>
              <div className="space-y-3">
                {/* Logo preview */}
                {logoPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={logoPreview}
                      alt="Aperçu du logo"
                      className="h-24 w-24 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 focus:outline-none"
                      title="Supprimer le logo"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-24 w-24 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}

                {/* File input */}
                <div className="relative">
                  <input
                    type="file"
                    id="merchant-logo"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="merchant-logo"
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {logoPreview ? 'Changer le logo' : 'Téléverser un logo'}
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  Formats acceptés : JPG, PNG, GIF, WebP. Max 10 Mo.
                </p>
              </div>
            </div>
          </DetailSection>

          {/* User Selection and Status (for new merchants) */}
          <DetailSection title="Configuration" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            
            {/* Corporate Hierarchy Fields - Global Brand Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marque globale
              </label>
              <OrganizationSelector
                value={formData.organization_id || ''}
                onChange={(organizationId) => {
                  setFormData(prev => ({
                    ...prev,
                    organization_id: organizationId,
                    // Clear region when organization changes
                    org_region_id: ''
                  }));
                }}
                placeholder="Sélectionnez une marque globale (optionnel)"
                className="w-full"
              />
              <p className="mt-1 text-xs text-gray-500">Laissez vide pour un commerçant indépendant</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Région/District
              </label>
              <RegionSelector
                organizationId={formData.organization_id || ''}
                value={formData.org_region_id || ''}
                onChange={(regionId) => {
                  setFormData(prev => ({
                    ...prev,
                    org_region_id: regionId
                  }));
                }}
                placeholder="Sélectionnez une région (optionnel)"
                className="w-full"
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.organization_id
                  ? 'Sélectionnez une région pour cette marque globale'
                  : 'Sélectionnez d\'abord une marque globale'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code magasin
              </label>
              <input
                type="text"
                name="store_code"
                value={storeCodeValidation.value}
                onChange={storeCodeValidation.handleChange}
                onBlur={storeCodeValidation.handleBlur}
                className={getInputClassName(
                  !!storeCodeValidation.error,
                  storeCodeValidation.isValid && storeCodeValidation.touched,
                  "w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                )}
                placeholder="Ex: STORE-001 (optionnel)"
              />
              {storeCodeValidation.error && (
                <p className="mt-1 text-sm text-red-600">{storeCodeValidation.error}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">Code interne pour identifier le magasin</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Magasin parent
              </label>
              <StoreSelector
                organizationId={formData.organization_id || ''}
                regionId={formData.org_region_id || ''}
                value={formData.parent_store_id || ''}
                onChange={(storeId) => {
                  setFormData(prev => ({
                    ...prev,
                    parent_store_id: storeId
                  }));
                }}
                placeholder="Sélectionnez un magasin parent (optionnel)"
                className="w-full"
                excludeStoreId={id} // Exclude current store in edit mode
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.organization_id
                  ? 'Sélectionnez un magasin parent dans la même organisation'
                  : 'Sélectionnez d\'abord une marque globale'}
              </p>
            </div>
            
            <div className="flex items-center pt-4">
              <input
                type="checkbox"
                name="is_active"
                id="is_active"
                checked={formData.is_active || false}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Commerçant actif
              </label>
            </div>
          </DetailSection>
        </div>

        {/* Address Information */}
        <DetailSection title="Adresse du commerce" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">

          {/* Google Maps Address Search */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher l'adresse avec Google Maps
              <span className="ml-1 text-xs font-normal text-gray-400 italic">(recommandé)</span>
            </label>
            <div className="relative">
              <GoogleMapsConfigProvider
                config={{
                  apiKey: 'AIzaSyBrNFuKMuE48eOWeg8V9Y9_3wRXO0KSJKQ',
                  libraries: ['places'],
                  language: 'fr'
                  // region removed to allow worldwide search
                }}
              >
                <GoogleMapsAddressPicker
                  onChange={handleAddressSelect}
                  placeholder="Recherchez une adresse (ex: 123 Rue de la Paix, Paris)"
                  onError={(error) => console.error('Google Maps error:', error)}
                />
              </GoogleMapsConfigProvider>
              <div className="mt-2 text-xs text-gray-500">
                Utilisez la recherche Google Maps pour trouver l'adresse exacte et éviter les erreurs de saisie.
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Address Line 1 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse ligne 1 *
              </label>
              <input
                type="text"
                name="address_line1"
                value={addressLine1Validation.value}
                onChange={addressLine1Validation.handleChange}
                onBlur={addressLine1Validation.handleBlur}
                className={getInputClassName(
                  !!addressLine1Validation.error,
                  addressLine1Validation.isValid && addressLine1Validation.touched,
                  "w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                )}
                placeholder="Ex: 123 Rue de la Paix"
              />
              {addressLine1Validation.error && (
                <p className="mt-1 text-sm text-red-600">{addressLine1Validation.error}</p>
              )}
            </div>

            {/* Address Line 2 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse ligne 2
                <span className="ml-1 text-xs font-normal text-gray-400 italic">(optionnel)</span>
              </label>
              <input
                type="text"
                name="address_line2"
                value={formData.address_line2 || ''}
                onChange={handleChange}
                className={getInputClassName(
                  false,
                  false,
                  "w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                )}
                placeholder="Ex: Appartement 4B, Étage 2"
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ville *
              </label>
              <input
                type="text"
                name="city"
                value={cityValidation.value}
                onChange={cityValidation.handleChange}
                onBlur={cityValidation.handleBlur}
                className={getInputClassName(
                  !!cityValidation.error,
                  cityValidation.isValid && cityValidation.touched,
                  "w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                )}
                placeholder="Ex: Paris"
              />
              {cityValidation.error && (
                <p className="mt-1 text-sm text-red-600">{cityValidation.error}</p>
              )}
            </div>

            {/* State/Region */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                État/Région
                <span className="ml-1 text-xs font-normal text-gray-400 italic">(optionnel)</span>
              </label>
              <input
                type="text"
                name="state"
                value={formData.state || ''}
                onChange={handleChange}
                className={getInputClassName(
                  false,
                  false,
                  "w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                )}
                placeholder="Ex: Île-de-France"
              />
            </div>

            {/* Postal Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code postal *
              </label>
              <input
                type="text"
                name="postal_code"
                value={postalCodeValidation.value}
                onChange={postalCodeValidation.handleChange}
                onBlur={postalCodeValidation.handleBlur}
                className={getInputClassName(
                  !!postalCodeValidation.error,
                  postalCodeValidation.isValid && postalCodeValidation.touched,
                  "w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                )}
                placeholder="Ex: 75001"
              />
              {postalCodeValidation.error && (
                <p className="mt-1 text-sm text-red-600">{postalCodeValidation.error}</p>
              )}
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pays *
              </label>
              <select
                name="country"
                value={countryValidation.value}
                onChange={countryValidation.handleChange}
                onBlur={countryValidation.handleBlur}
                className={getInputClassName(
                  !!countryValidation.error,
                  countryValidation.isValid && countryValidation.touched,
                  "w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                )}
              >
                <option value="">Sélectionnez un pays</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.code}>
                    {country.flag_emoji} {country.name}
                  </option>
                ))}
              </select>
              {countryValidation.error && (
                <p className="mt-1 text-sm text-red-600">{countryValidation.error}</p>
              )}
            </div>
          </div>

          {/* Map Display */}
          {showMap && formData.latitude && formData.longitude && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center mb-4">
                <MapPin className="h-5 w-5 text-gray-400 mr-2" />
                <h3 className="text-md font-semibold text-gray-900">Carte de localisation</h3>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <GoogleMapsConfigProvider
                  config={{
                    apiKey: 'AIzaSyBrNFuKMuE48eOWeg8V9Y9_3wRXO0KSJKQ',
                    libraries: ['places'],
                    language: 'fr'
                    // region removed to allow worldwide search
                  }}
                >
                  <GoogleMapsStaticMap
                    latitude={formData.latitude}
                    longitude={formData.longitude}
                    zoom={15}
                    width={600}
                    height={300}
                    markerColor="red"
                    apiKey="AIzaSyBrNFuKMuE48eOWeg8V9Y9_3wRXO0KSJKQ"
                  />
                </GoogleMapsConfigProvider>
                <div className="mt-3 text-sm text-gray-600">
                  <p>Localisation: {formData.latitude?.toFixed(6)}, {formData.longitude?.toFixed(6)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    La carte montre l'emplacement exact du commerce basé sur l'adresse sélectionnée.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Advanced Geolocation Section */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setShowAdvancedGeolocation(!showAdvancedGeolocation)}
              className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              <ChevronDown className={`h-4 w-4 mr-2 transition-transform ${showAdvancedGeolocation ? 'rotate-180' : ''}`} />
              Géolocalisation avancée
            </button>
            
            {showAdvancedGeolocation && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Latitude
                    <span className="ml-1 text-xs font-normal text-gray-400 italic">(optionnel)</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    name="latitude"
                    value={formData.latitude || ''}
                    onChange={handleChange}
                    className={getInputClassName(
                      false,
                      false,
                      "w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                    )}
                    placeholder="Ex: 48.8566"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Longitude
                    <span className="ml-1 text-xs font-normal text-gray-400 italic">(optionnel)</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    name="longitude"
                    value={formData.longitude || ''}
                    onChange={handleChange}
                    className={getInputClassName(
                      false,
                      false,
                      "w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                    )}
                    placeholder="Ex: 2.3522"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Is Primary Address Checkbox */}
          <div className="mt-6 flex items-center">
            <input
              type="checkbox"
              name="is_primary"
              id="is_primary"
              checked={formData.is_primary || false}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="is_primary" className="ml-2 block text-sm text-gray-900">
              Adresse principale
            </label>
          </div>
        </DetailSection>

        {/* Form Actions */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/merchants')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                {isEditMode ? 'Modification...' : 'Création...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 inline mr-2" />
                {isEditMode ? 'Modifier le commerçant' : 'Créer le commerçant'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default MerchantForm
